import { useQuery } from '@apollo/client';
import {
  T,
  B1,
  DisabledTag,
  getTranslatedText,
  getDownloadURL,
  ALL_MODULES,
  Loader,
  OPERATORMAP,
  AccessControl,
  COMMON_CONSTANTS,
  DEFAULT_FIELDS_BY_COLUMN_NAME
} from '@nile/components';
import {
  Avatar,
  DisplayText,
  Heading,
  TextStyle
} from '@superops/ui-design-system';
import { GET_TICKET_PRIORITY_FIELDS } from '@nile/banyan/src/Graphql/Mutations/ticket';
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { usePagination, useTable } from 'react-table';
import { isEmpty } from 'lodash';
import nanoid from 'nanoid';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { AccountClient, GET_ENTITY_PROPERTIES, GET_FIELD } from '@nile/graphql';

const CombinatorTag = styled.div`
  .combinator {
    background: ${(props) => (props.value === 'OR' ? '#E65D0F' : '#6C69FF')};
  }
  .triangle-right {
    border-left: 10px solid
      ${(props) => (props.value === 'OR' ? '#E65D0F' : '#6C69FF')};
  }
`;

function SLAViewMode(props) {
  const { intl, sla } = props;
  const {
    executeForAllClients,
    associatedClients: clients = [], // seting default value to array
    slaTiming,
    slaEscalationAction,
    slaCondition
  } = sla;
  const addIds = (ruleGroup, type = 'group') => {
    const ruleGroupCopy = { ...ruleGroup };
    ruleGroupCopy.id = type === 'group' ? `g-${nanoid()}` : `r-${nanoid()}`;
    if (ruleGroupCopy.operands) {
      ruleGroupCopy.operands = ruleGroupCopy.operands.map((rule) =>
        addIds(rule, 'rule')
      );
    }
    return ruleGroupCopy;
  };
  const [slaConditions, setSlaConditions] = useState(slaCondition || {});
  const [slaRule, setSLARule] = useState(addIds(slaConditions));

  const { loading, data, error } = useQuery(GET_ENTITY_PROPERTIES, {
    client: AccountClient
  });

  useEffect(() => {
    setSlaConditions(slaCondition);
    setSLARule(addIds(slaCondition));
  }, [slaCondition]);

  const isRuleGroup = (ruleOrGroup) => {
    return !!(ruleOrGroup?.joinOperator && ruleOrGroup?.operands);
  };

  const getLevel = (i, index = 0, query = slaRule) => {
    let foundAtIndex = -1;
    if (query?.id === i) {
      foundAtIndex = index;
    } else if (isRuleGroup(query)) {
      query.operands.forEach((rule) => {
        if (foundAtIndex === -1) {
          let indexForRule = index;
          if (isRuleGroup(rule)) indexForRule++;
          foundAtIndex = getLevel(i, indexForRule, rule);
        }
      });
    }
    return foundAtIndex;
  };

  return (
    <div className="scroll ch-90">
      <AccessControl
        productToCheck={COMMON_CONSTANTS.MSP}
        render={
          <div className="mb-40">
            <div>
              <T>
                <div className="mb-10">
                  <Heading element="h3" weight="SemiBold">
                    <FormattedMessage id="admin.slaPolicy.h2.executeFor" />
                  </Heading>
                </div>
              </T>
              <div>
                <div className="flex wrap mt-10">
                  {clients.length > 0 ? (
                    clients.map((el) => {
                      const module = ALL_MODULES.clientAvatar;
                      const details = {
                        fileName: el.logoFileName,
                        clientId: el.accountId
                      };
                      const downloadUrl = getDownloadURL({ module, details });
                      return el.logoFileName ? (
                        <div className="flex center mb-10 fb-33">
                          <img
                            width="30px"
                            height="30px"
                            className="overflow-hidden br50pt"
                            src={downloadUrl}
                            url={downloadUrl}
                          />
                          <div className="ml8">
                            <DisplayText size="large" weight="Medium">
                              {el.name}
                            </DisplayText>
                          </div>
                        </div>
                      ) : (
                        <div className="flex center mb-10 fb-33">
                          <Avatar customer size="small" />
                          <div className="ml8">
                            <DisplayText size="large" weight="Medium">
                              {el.name}
                            </DisplayText>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <FormattedMessage id="admin.sla.associatedClients.emptyState" />
                  )}
                </div>
              </div>
            </div>
          </div>
        }
      />
      <div className="mb-40">
        <div className="mb-10">
          <Heading element="h3" weight="SemiBold">
            <FormattedMessage id="admin.slaPolicy.h2.conditions" />
          </Heading>
        </div>
        {loading ? (
          <Loader />
        ) : (
          <RuleGroup
            id={slaRule.id}
            joinOperator={slaRule.joinOperator}
            operands={slaRule.operands}
            getLevel={getLevel}
            intl={intl}
            entitiesMeta={data.getEntitiesMeta}
          />
        )}
      </div>
      <div className="mb-40">
        <div className="mb-10">
          <Heading element="h3" weight="SemiBold">
            <FormattedMessage id="admin.slaPolicy.h2.taregtAndEscalation" />
          </Heading>
        </div>
        {!slaTiming?.length ? (
          <TextStyle weight="SemiBold">
            <FormattedMessage id="admin.slaPolicy.noEscalations" />
          </TextStyle>
        ) : (
          <EscalationTableView slaTiming={slaTiming} intl={intl} />
        )}
      </div>
      <div className="mb-40">
        <div className="mb-10">
          <Heading element="h3" weight="SemiBold">
            <FormattedMessage id="admin.slaPolicy.h2.thenPerform" />
          </Heading>
        </div>
        {/* <div>
          <div className="flex center">
            <BellIcon />
            <div className="ml8">Escalate to</div>

          </div>
        </div> */}
        <div>
          <div className="flex center">
            {!slaEscalationAction?.techGroup &&
            !slaEscalationAction?.technician ? (
              <B1>
                <FormattedMessage id="admin.slaPolicy.noAssignments" />
              </B1>
            ) : (
              <>
                <SVGIcon name="AssignToMeIcon" />

                <div className="ml8">
                  <FormattedMessage id="admin.slaPolicy.assignTo" />
                </div>
                {slaEscalationAction?.techGroup && (
                  <div className="ml8 flex center">
                    <Avatar customer size="extraSmall" />
                    <B1 className="ml8">
                      {slaEscalationAction.techGroup.name}
                    </B1>
                  </div>
                )}
                {slaEscalationAction?.technician && (
                  <div className="flex center">
                    <div className="techAvatar ml8">
                      <Avatar
                        size="extraSmall"
                        initials={`${slaEscalationAction?.technician?.firstName?.[0]}${slaEscalationAction?.technician?.lastName?.[0]}`}
                      />
                    </div>
                    <B1 className="ml8">{`${slaEscalationAction?.technician?.firstName} ${slaEscalationAction?.technician?.lastName}`}</B1>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RuleGroup(props) {
  const { operands, id, getLevel, joinOperator, intl, entitiesMeta } = props;
  const isRuleGroup = (ruleOrGroup) => {
    return !!(ruleOrGroup?.joinOperator && ruleOrGroup?.operands);
  };
  const level = getLevel(id);

  const getModifiedAttribute = (attribute) => {
    const attributeParts = attribute.split('.');
    const parentEntity =
      attributeParts.length > 1 ? attributeParts[0] : 'ticket';

    const entityAttributes = entitiesMeta?.find(
      ({ name }) =>
        name.toLowerCase() ===
        (parentEntity === 'requester'
          ? 'clientUser'
          : parentEntity
        ).toLowerCase()
    )?.attributes;

    const attributeDetails = entityAttributes?.find(
      ({ name }) =>
        name === (parentEntity === 'ticket' ? attribute : attributeParts[1])
    );

    if (attributeDetails?.isCustom) {
      return attributeDetails.label;
    }

    return attribute.indexOf('.') > -1
      ? attribute.split('.').join(' ')
      : attribute;
  };

  return operands.map((r, idx) => (
    <div key={r.id}>
      {idx ? (
        level === 0 ? (
          <CombinatorTag value={joinOperator} className="flex mt8 mb8">
            <div className="plr-8 text-white uppercase combinator">
              <DisplayText size="medium" weight="Bold">
                <TextStyle> {joinOperator}</TextStyle>
              </DisplayText>
            </div>

            <div className="triangle-right"></div>
          </CombinatorTag>
        ) : (
          <CombinatorTag value={joinOperator} className="flex mt8 mb8">
            <div className="plr-8 text-white uppercase combinator">
              <DisplayText size="medium" weight="Bold">
                <TextStyle> {joinOperator}</TextStyle>
              </DisplayText>
            </div>
            <div className="triangle-right"></div>
          </CombinatorTag>
        )
      ) : null}
      {isRuleGroup(r) ? (
        <div className="border p8 rounded-4px">
          <RuleGroup
            id={r.id}
            parentId={id}
            joinOperator={r.joinOperator}
            operands={r.operands}
            getLevel={getLevel}
            intl={intl}
            entitiesMeta={entitiesMeta}
          />
        </div>
      ) : (
        <Rule
          id={r.id}
          parentId={id}
          attribute={getModifiedAttribute(r.attribute)}
          value={r.value}
          operator={r.operator}
          operands={operands}
          index={idx}
          intl={intl}
        />
      )}
    </div>
  ));
}

function Rule(props) {
  const { rule, attribute, operator, value, intl } = props;
  const operatorMap = OPERATORMAP(intl);

  return (
    <div className="fx actr">
      <div className="attribute capitalize ml8 bold">{attribute}</div>
      <div className="operator ml8">{operatorMap[operator]}</div>
      <div className="value ml8 bold">
        {typeof value === 'object' ? value.join(', ') : value}
      </div>
    </div>
  );
}

function Table({ columns, data, updateMyData, skipPageReset }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      autoResetPage: !skipPageReset,
      updateMyData
    },
    usePagination
  );

  return (
    <>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function EscalationTableView(props) {
  const { slaTiming, intl } = props;
  const {
    loading: priorityLoading,
    error: errorPriority,
    data: priorityFields
  } = useQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY
      }
    }
  });
  const t_data = slaTiming?.length ? slaTiming : [];
  const [timeData, setData] = useState(t_data);
  useEffect(() => {
    setData(slaTiming.length ? slaTiming : []);
  }, [slaTiming]);
  const durMap = {
    HOUR: 'h',
    MINUTE: 'm'
  };
  const EXECUTION_TIME = [
    {
      name: 'BUSINESS_HOURS',
      label: getTranslatedText(intl, 'admin.slaPolicy.executeIn.businessHrs'),
      value: 'Business hours'
    },
    {
      name: 'CALENDAR_HOURS',
      label: getTranslatedText(intl, 'admin.slaPolicy.executeIn.calendarHrs'),
      value: 'Calendat hours 24*7'
    }
  ];

  const ESCALATION_TABLE_FORM = {
    cols: [
      {
        name: 'priorityOption',
        label: ' ',
        children: [
          {
            name: 'priority',
            accessor: 'priorityOption',
            label: getTranslatedText(
              intl,
              'admin.slaPolicy.priority.tblHeader'
            ),
            type: 'priority'
          }
        ]
      },
      {
        name: 'first_response',
        label: getTranslatedText(
          intl,
          'admin.slaPolicy.firstResponse.tblHeader'
        ),
        children: [
          {
            name: 'firstResponseTarget',
            label: getTranslatedText(
              intl,
              'admin.slaPolicy.firstResponseTarget.tblHeader'
            ),
            type: 'popover'
          },
          {
            name: 'firstResponseEscalation',
            label: getTranslatedText(
              intl,
              'admin.slaPolicy.firstResponseEscalation.tblHeader'
            ),
            type: 'popover'
          }
        ]
      },
      {
        name: 'resolution',
        label: getTranslatedText(intl, 'admin.slaPolicy.resolution.tblHeader'),
        children: [
          {
            name: 'resolutionTarget',
            label: getTranslatedText(
              intl,
              'admin.slaPolicy.resolutionTarget.tblHeader'
            ),
            type: 'popover'
          },
          {
            name: 'resolutionEscalation',
            label: getTranslatedText(
              intl,
              'admin.slaPolicy.resolutionEscalation.tblHeader'
            ),
            type: 'popover'
          }
        ]
      },
      {
        name: 'execution',
        label: ' ',
        children: [
          {
            name: 'executeIn',
            label: getTranslatedText(
              intl,
              'admin.slaPolicy.executeIn.tblHeader'
            ),
            type: 'dropdown',
            values: EXECUTION_TIME
          }
        ]
      }
    ]
  };
  const Styles = styled.div`
    table {
      border-spacing: 0;

      tr {
        :last-child {
          td {
            border-bottom: 0;
          }
        }
      }
      thead {
        box-shadow: none;
      }
      th,
      td {
        margin: 0;
        padding: 0.5rem;
        box-shadow: none;
        text-transform: none;
        :last-child {
          border-right: 0;
        }
      }
    }

    .pagination {
      padding: 0.5rem;
    }
  `;

  const getDurationValue = (duration) => {
    let str = '';
    for (let i = 0; i < duration.length; i++) {
      str = str + duration[i].quantity + durMap[duration[i].unit] + ' ';
    }
    return str;
  };
  const getDynamicCell = (rowObject, childColumn, cellRef) => {
    const {
      value: initialValue,
      row: { index },
      column: { id } // This is a custom function that we supplied to our table instance
    } = rowObject;
    switch (childColumn.type) {
      case 'priority':
        const options = priorityFields?.getField?.options;
        const val = options && options.find((el) => el.id === initialValue?.id);
        return (
          <div className="flex center">
            <div
              className="indicator"
              style={{ background: val?.details?.colorCode || '#ececec' }}
            ></div>
            {val?.value}
          </div>
        );
      case 'popover':
        return isEmpty(initialValue) ? (
          <div className="flex center hidden bold">
            <DisabledTag>
              <FormattedMessage id="app.noData.message" />
            </DisabledTag>
          </div>
        ) : (
          <div className="flex center">
            {initialValue.type === 'FIXED' ? (
              <div className="flex center bold">
                <span>{getDurationValue(initialValue.fixedDuration)}</span>
              </div>
            ) : (
              initialValue.relativeDuration
            )}
          </div>
        );
      case 'dropdown':
        return (
          <div>
            {childColumn.values.find(
              (el) => el.name === timeData[index].executeIn
            )?.label || (
              <DisabledTag>
                <FormattedMessage id="app.noData.message" />
              </DisabledTag>
            )}
          </div>
        );
      default:
        return <div>Hi Vignesh!!</div>;
    }
  };
  const columns = React.useMemo(() => {
    return ESCALATION_TABLE_FORM.cols.map((column) => {
      return {
        Header: column.label,
        columns: column.children.map((childColumn) => {
          return {
            Header: childColumn.label,
            accessor: childColumn?.accessor || childColumn.name,
            Cell: (row) => {
              const cellRef = useRef({});
              return getDynamicCell(row, childColumn, cellRef);
            }
          };
        })
      };
    });
  });
  if (priorityLoading) return <div>loading...</div>;
  return (
    <Styles name="Esclations">
      <Table columns={columns} data={timeData} />
    </Styles>
  );
}

export default injectIntl(SLAViewMode);
